import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import closeBttn from './assets/closeBttnSquare.svg';
import nextBttn from './assets/nextBttn.svg';
import WellnessGymCard from './WellnessGymCard';
import './css/WellnessGym.css';
import {
    CUSTOM_WORKOUT_PILLARS,
    STEP_TYPES,
    loadCustomWorkouts,
    saveCustomWorkouts,
    loadDraft,
    saveDraft,
    clearDraft,
    defaultStep,
    normalizeStepsOrder,
    estimateWorkoutMinutesFromSteps,
    validateCustomWorkoutForSave,
    newWorkoutId,
    hasReflectionClose,
    emptyCwDraft,
} from './customWorkouts';

/** PopupForm `.formContainer` uses translate; parent is flex — reset for true centering */
const centeredFormContainerStyle = {
    top: 'auto',
    left: 'auto',
    transform: 'none',
    margin: 0,
    alignSelf: 'center',
};

function reorderDraggableSteps(steps, fromIdx, toIdx) {
    const norm = normalizeStepsOrder(steps);
    const refIdx = norm.findIndex((s) => s.type === STEP_TYPES.REFLECTION_CLOSE);
    const maxIdx = refIdx === -1 ? norm.length - 1 : refIdx - 1;
    if (fromIdx < 0 || toIdx < 0 || fromIdx > maxIdx || toIdx > maxIdx || fromIdx === toIdx) return norm;
    const arr = [...norm];
    const [moved] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, moved);
    return normalizeStepsOrder(arr);
}

export function WellnessGymCustomWorkoutFeature({
    username,
    token,
    gymSubmitUrl,
    apiKeyHeader,
    convertTimestampToFullDateString,
    triggerFlowShellStyle,
    triggerFlowScrollStyle,
    triggerFlowFooterStyle,
    triggerFieldStyle,
    nsrBoxPhases,
    nsrExtPhases,
}) {
    const [savedWorkouts, setSavedWorkouts] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalView, setModalView] = useState(null);
    const [builderPhase, setBuilderPhase] = useState(1);
    const [draft, setDraft] = useState(emptyCwDraft);
    const [draftPromptOpen, setDraftPromptOpen] = useState(false);
    const [playerWorkout, setPlayerWorkout] = useState(null);
    const [playerPreview, setPlayerPreview] = useState(false);
    const [playerStepIndex, setPlayerStepIndex] = useState(0);
    const [playerResponses, setPlayerResponses] = useState([]);
    const playerStartedAtRef = useRef(0);
    const playerLoggedRef = useRef(false);
    const [pvSlider, setPvSlider] = useState(5);
    const [pvMc, setPvMc] = useState([]);
    const [pvText, setPvText] = useState('');
    const [breathDone, setBreathDone] = useState(false);
    const [breathUi, setBreathUi] = useState({ label: 'Inhale', scale: 1, cycle: 0 });
    const [breathRepeatNonce, setBreathRepeatNonce] = useState(0);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [exitSessionConfirm, setExitSessionConfirm] = useState(false);
    const breathCancelRef = useRef(null);

    const refreshSaved = useCallback(() => {
        setSavedWorkouts(loadCustomWorkouts(username));
    }, [username]);

    useEffect(() => {
        refreshSaved();
    }, [refreshSaved]);

    useEffect(() => {
        if (!modalOpen || modalView !== 'builder') return undefined;
        const t = setTimeout(() => saveDraft(username, draft), 300);
        return () => clearTimeout(t);
    }, [modalOpen, modalView, username, draft]);

    useEffect(() => {
        if (modalView !== 'player' || !playerWorkout) return;
        setPvSlider(5);
        setPvMc([]);
        setPvText('');
        setBreathDone(false);
        setBreathUi({ label: 'Inhale', scale: 1, cycle: 0 });
        if (breathCancelRef.current) {
            breathCancelRef.current();
            breathCancelRef.current = null;
        }
    }, [modalView, playerWorkout, playerStepIndex]);

    const currentPlayerStep = playerWorkout?.steps?.[playerStepIndex];

    useEffect(() => {
        if (modalView !== 'player' || !playerWorkout) return undefined;
        const step = playerWorkout.steps[playerStepIndex];
        if (!step || step.type !== STEP_TYPES.BREATH_PACER) return undefined;
        let cancelled = false;
        const phases = step.config.pattern === 'extendedExhale' ? nsrExtPhases : nsrBoxPhases;
        const cycles = Math.min(5, Math.max(1, Number(step.config.cycles) || 1));
        async function run() {
            for (let c = 0; c < cycles; c += 1) {
                for (let p = 0; p < phases.length; p += 1) {
                    if (cancelled) return;
                    const ph = phases[p];
                    setBreathUi({ label: ph.label, scale: ph.scale, cycle: c + 1 });
                    await new Promise((r) => {
                        setTimeout(r, ph.ms);
                    });
                }
            }
            if (!cancelled) setBreathDone(true);
        }
        run();
        breathCancelRef.current = () => {
            cancelled = true;
        };
        return () => {
            cancelled = true;
        };
    }, [modalView, playerWorkout, playerStepIndex, nsrBoxPhases, nsrExtPhases, breathRepeatNonce]);

    function openModal() {
        const el = document.getElementById('custom-workout-modal');
        if (el) {
            el.style.display = 'flex';
            el.style.visibility = 'visible';
        }
        setModalOpen(true);
    }

    function hideModalDom() {
        const el = document.getElementById('custom-workout-modal');
        if (el) {
            el.style.display = 'none';
            el.style.visibility = 'hidden';
        }
    }

    function closeModalInternal() {
        setModalOpen(false);
        setModalView(null);
        setBuilderPhase(1);
        setDraftPromptOpen(false);
        setPlayerWorkout(null);
        setPlayerPreview(false);
        setPlayerStepIndex(0);
        setPlayerResponses([]);
        playerStartedAtRef.current = 0;
        playerLoggedRef.current = false;
        setExitSessionConfirm(false);
        hideModalDom();
    }

    function requestCloseModal() {
        if (modalView === 'player' && playerWorkout && !playerPreview && !playerLoggedRef.current && playerStartedAtRef.current) {
            setExitSessionConfirm(true);
            return;
        }
        closeModalInternal();
    }

    function confirmExitPlayer() {
        if (!playerPreview && playerWorkout) {
            const ts = Date.now();
            axios
                .post(
                    gymSubmitUrl,
                    {
                        username,
                        token,
                        date: convertTimestampToFullDateString(ts),
                        exerciseSubmission: {
                            content: '',
                            mode: 'text',
                            exercise_type: 'custom_workout_session',
                            isCustom: true,
                            userId: username,
                            workoutId: playerWorkout.id,
                            startedAt: playerStartedAtRef.current,
                            completedAt: ts,
                            responses: playerResponses,
                            abandoned_at: playerWorkout.steps[playerStepIndex]?.id || 'mid_session',
                            timestamp: ts,
                        },
                    },
                    { headers: apiKeyHeader }
                )
                .catch((err) => console.error('custom workout abandon submit failed', err));
        }
        setExitSessionConfirm(false);
        closeModalInternal();
    }

    function beginCreateWorkout() {
        const d = loadDraft(username);
        if (d && (d.name || (d.steps && d.steps.length > 0))) {
            setDraftPromptOpen(true);
            setBuilderPhase(1);
            setModalView('builder');
            openModal();
            return;
        }
        setDraftPromptOpen(false);
        setDraft(emptyCwDraft());
        setBuilderPhase(1);
        setModalView('builder');
        openModal();
    }

    function continueDraft() {
        const d = loadDraft(username);
        const base = d && typeof d === 'object' ? d : {};
        setDraft({
            ...emptyCwDraft(),
            ...base,
            steps: Array.isArray(base.steps) ? base.steps : [],
            editingId: base.editingId || null,
        });
        setDraftPromptOpen(false);
        setBuilderPhase(1);
        setModalView('builder');
        openModal();
    }

    function discardDraftAndCreate() {
        clearDraft(username);
        setDraft(emptyCwDraft());
        setDraftPromptOpen(false);
        setBuilderPhase(1);
        setModalView('builder');
        openModal();
    }

    function editWorkout(w) {
        setPlayerWorkout(null);
        setPlayerPreview(false);
        setPlayerStepIndex(0);
        setPlayerResponses([]);
        playerLoggedRef.current = false;
        playerStartedAtRef.current = 0;
        setDraft({
            name: w.name,
            description: w.description || '',
            pillar: w.pillar,
            steps: JSON.parse(JSON.stringify(w.steps || [])),
            editingId: w.id,
        });
        setBuilderPhase(1);
        setModalView('builder');
        setDraftPromptOpen(false);
        openModal();
    }

    function startPlayer(w, preview) {
        setPlayerWorkout(w);
        setPlayerPreview(!!preview);
        setPlayerStepIndex(0);
        setPlayerResponses([]);
        playerLoggedRef.current = false;
        playerStartedAtRef.current = Date.now();
        setModalView('player');
        setDraftPromptOpen(false);
        openModal();
    }

    function addStepType(type) {
        setDraft((d) => {
            let steps = [...(d.steps || [])];
            if (steps.length >= 6) return d;
            if (type === STEP_TYPES.REFLECTION_CLOSE) {
                steps = steps.filter((s) => s.type !== STEP_TYPES.REFLECTION_CLOSE);
                const ns = defaultStep(STEP_TYPES.REFLECTION_CLOSE);
                steps.push(ns);
                return { ...d, steps: normalizeStepsOrder(steps) };
            }
            const ref = steps.find((s) => s.type === STEP_TYPES.REFLECTION_CLOSE);
            const withoutRef = steps.filter((s) => s.type !== STEP_TYPES.REFLECTION_CLOSE);
            if (withoutRef.length >= 5) return d;
            withoutRef.push(defaultStep(type));
            if (ref) withoutRef.push(ref);
            return { ...d, steps: normalizeStepsOrder(withoutRef) };
        });
    }

    function removeStep(id) {
        setDraft((d) => ({ ...d, steps: normalizeStepsOrder((d.steps || []).filter((s) => s.id !== id)) }));
    }

    function updateStepConfig(id, patch) {
        setDraft((d) => ({
            ...d,
            steps: (d.steps || []).map((s) => (s.id === id ? { ...s, config: { ...s.config, ...patch } } : s)),
        }));
    }

    function onDropReorder(toIdx, fromIdx) {
        setDraft((d) => ({ ...d, steps: reorderDraggableSteps(d.steps || [], fromIdx, toIdx) }));
    }

    function saveWorkoutToStorage() {
        const steps = normalizeStepsOrder(draft.steps || []);
        const w = {
            id: draft.editingId || newWorkoutId(),
            name: draft.name.trim(),
            description: (draft.description || '').trim(),
            pillar: draft.pillar,
            estimatedMinutes: estimateWorkoutMinutesFromSteps(steps),
            isCustom: true,
            createdBy: username,
            steps,
            createdAt: draft.editingId ? savedWorkouts.find((x) => x.id === draft.editingId)?.createdAt || Date.now() : Date.now(),
            updatedAt: Date.now(),
        };
        const next = draft.editingId ? savedWorkouts.map((x) => (x.id === draft.editingId ? w : x)) : [w, ...savedWorkouts];
        saveCustomWorkouts(username, next);
        setSavedWorkouts(next);
        clearDraft(username);
        closeModalInternal();
    }

    function deleteWorkout(id) {
        const next = savedWorkouts.filter((x) => x.id !== id);
        saveCustomWorkouts(username, next);
        setSavedWorkouts(next);
        setDeleteConfirmId(null);
        if (playerWorkout?.id === id) {
            closeModalInternal();
        }
    }

    function finishPlayerSession(mergedResponses) {
        if (playerPreview) {
            closeModalInternal();
            return;
        }
        const ts = Date.now();
        const responses = mergedResponses != null ? mergedResponses : playerResponses;
        axios
            .post(
                gymSubmitUrl,
                {
                    username,
                    token,
                    date: convertTimestampToFullDateString(ts),
                    exerciseSubmission: {
                        content: '',
                        mode: 'text',
                        exercise_type: 'custom_workout_session',
                        isCustom: true,
                        userId: username,
                        workoutId: playerWorkout.id,
                        startedAt: playerStartedAtRef.current,
                        completedAt: ts,
                        responses,
                        abandoned_at: null,
                        timestamp: ts,
                    },
                },
                { headers: apiKeyHeader }
            )
            .catch((err) => console.error('custom workout session submit failed', err));
        playerLoggedRef.current = true;
        closeModalInternal();
    }

    function advancePlayer(nextResponses) {
        const merged = [...playerResponses, ...nextResponses];
        setPlayerResponses(merged);
        if (playerStepIndex + 1 >= (playerWorkout.steps || []).length) {
            if (!playerPreview) finishPlayerSession(merged);
            else closeModalInternal();
            return;
        }
        setPlayerStepIndex((i) => i + 1);
    }

    const estMin = estimateWorkoutMinutesFromSteps(draft.steps || []);
    const validation = validateCustomWorkoutForSave({
        name: draft.name,
        description: draft.description,
        pillar: draft.pillar,
        steps: draft.steps,
    });

    const stepListForAdd = draft.steps || [];
    const hasRef = hasReflectionClose(stepListForAdd);
    const nonRefCount = stepListForAdd.filter((s) => s.type !== STEP_TYPES.REFLECTION_CLOSE).length;
    const addNonReflectionDisabled = stepListForAdd.length >= 6 || nonRefCount >= 5;
    const addReflectionDisabled = hasRef || stepListForAdd.length >= 6;

    const renderBuilder = () => (
        <div style={triggerFlowShellStyle}>
            {builderPhase > 1 ? (
                <div className="popupButtonGroup" style={{ position: 'static', width: '100%', flexShrink: 0, marginTop: 0, marginBottom: '0.25rem' }}>
                    <button type="button" className="popupNavigationBttn" onClick={() => setBuilderPhase((p) => Math.max(1, p - 1))} style={{ border: 'none', background: 'none' }}>
                        <img className="previousBttn" src={nextBttn} alt="back" />
                    </button>
                </div>
            ) : null}
            {draftPromptOpen ? (
                <div style={triggerFlowScrollStyle}>
                    <p style={{ textAlign: 'left', lineHeight: 1.5 }}>You have an unfinished workout. Continue building?</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
                        <button type="button" className="popupButton" onClick={continueDraft}>
                            Continue
                        </button>
                        <button type="button" className="popupButton" style={{ background: '#6a6a6a' }} onClick={discardDraftAndCreate}>
                            Discard
                        </button>
                    </div>
                </div>
            ) : builderPhase === 1 ? (
                <>
                    <div style={triggerFlowScrollStyle}>
                        <p className="questionInfo" style={{ textAlign: 'left', opacity: 0.7 }}>Workout Identity</p>
                        <h2 style={{ marginTop: '0.2rem', fontSize: '1.05rem', fontWeight: 600 }}>Name &amp; pillar</h2>
                        <input
                            value={draft.name}
                            maxLength={60}
                            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value.slice(0, 60) }))}
                            placeholder="Workout name (required)"
                            style={{ ...triggerFieldStyle, padding: '0.5rem', borderRadius: '0.4rem', marginTop: '0.5rem' }}
                        />
                        <textarea
                            value={draft.description}
                            maxLength={140}
                            rows={2}
                            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value.slice(0, 140) }))}
                            placeholder="Short description (optional)"
                            style={{ ...triggerFieldStyle, padding: '0.5rem', borderRadius: '0.4rem', marginTop: '0.45rem', resize: 'vertical' }}
                        />
                        <p className="questionInfo" style={{ textAlign: 'left', marginTop: '0.5rem' }}>Pillar</p>
                        <select
                            value={draft.pillar}
                            onChange={(e) => setDraft((d) => ({ ...d, pillar: e.target.value }))}
                            style={{ ...triggerFieldStyle, padding: '0.45rem', borderRadius: '0.4rem', marginTop: '0.25rem' }}
                        >
                            {CUSTOM_WORKOUT_PILLARS.map((p) => (
                                <option key={p} value={p}>
                                    {p}
                                </option>
                            ))}
                        </select>
                        <p className="questionInfo" style={{ textAlign: 'left', marginTop: '0.75rem', opacity: 0.75 }}>
                            Estimated time: ~{estMin} min
                        </p>
                    </div>
                    <div style={triggerFlowFooterStyle}>
                        <button type="button" className="popupButton" disabled={!draft.name.trim()} style={{ opacity: draft.name.trim() ? 1 : 0.45 }} onClick={() => setBuilderPhase(2)}>
                            Continue
                        </button>
                    </div>
                </>
            ) : builderPhase === 2 ? (
                <>
                    <div style={triggerFlowScrollStyle}>
                        <p className="questionInfo" style={{ textAlign: 'left', opacity: 0.7 }}>Step Builder</p>
                        <h2 style={{ marginTop: '0.2rem', fontSize: '1.02rem', fontWeight: 600 }}>Add blocks</h2>
                        <p className="questionInfo" style={{ textAlign: 'left', opacity: 0.72, marginBottom: '0.5rem' }}>
                            Max 6 steps. Reflection Close is required and stays last. ~{estMin} min
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.65rem' }}>
                            {[
                                { t: STEP_TYPES.SLIDER, label: 'Check-In Slider' },
                                { t: STEP_TYPES.MULTIPLE_CHOICE, label: 'Multiple Choice' },
                                { t: STEP_TYPES.TEXT_INPUT, label: 'Text Input' },
                                { t: STEP_TYPES.BREATH_PACER, label: 'Breath Pacer' },
                                { t: STEP_TYPES.REFLECTION_CLOSE, label: 'Reflection Close' },
                            ].map((b) => (
                                <button
                                    key={b.t}
                                    type="button"
                                    onClick={() => addStepType(b.t)}
                                    disabled={b.t === STEP_TYPES.REFLECTION_CLOSE ? addReflectionDisabled : addNonReflectionDisabled}
                                    style={{
                                        fontSize: '0.72rem',
                                        padding: '0.35rem 0.45rem',
                                        borderRadius: '0.35rem',
                                        border: '1px solid #c4c2bf',
                                        background: '#EFEEED',
                                        cursor: 'pointer',
                                        opacity: b.t === STEP_TYPES.REFLECTION_CLOSE ? (addReflectionDisabled ? 0.45 : 1) : addNonReflectionDisabled ? 0.45 : 1,
                                    }}
                                >
                                    + {b.label}
                                </button>
                            ))}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {(draft.steps || []).map((step, idx) => {
                                const refIdx = (draft.steps || []).findIndex((s) => s.type === STEP_TYPES.REFLECTION_CLOSE);
                                const isLocked = step.type === STEP_TYPES.REFLECTION_CLOSE;
                                const draggable = !isLocked && (refIdx === -1 || idx < refIdx);
                                return (
                                    <div
                                        key={step.id}
                                        draggable={draggable}
                                        onDragStart={(e) => {
                                            e.dataTransfer.setData('text/plain', String(idx));
                                            e.dataTransfer.effectAllowed = 'move';
                                        }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            const from = Number(e.dataTransfer.getData('text/plain'));
                                            onDropReorder(idx, from);
                                        }}
                                        style={{
                                            border: '1px solid #d8d6d3',
                                            borderRadius: '0.45rem',
                                            padding: '0.5rem',
                                            background: '#f7f6f4',
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.35rem' }}>
                                            <span style={{ fontSize: '0.72rem', opacity: 0.65 }}>
                                                {draggable ? '⋮⋮ drag' : 'locked'} · {step.type}
                                            </span>
                                            <button type="button" onClick={() => removeStep(step.id)} style={{ border: 'none', background: 'none', fontSize: '0.78rem' }}>
                                                Remove
                                            </button>
                                        </div>
                                        {step.type === STEP_TYPES.SLIDER ? (
                                            <div style={{ marginTop: '0.35rem' }}>
                                                <input
                                                    value={step.config.question}
                                                    maxLength={80}
                                                    onChange={(e) => updateStepConfig(step.id, { question: e.target.value.slice(0, 80) })}
                                                    placeholder="Question (max 80)"
                                                    style={{ ...triggerFieldStyle, padding: '0.35rem', marginBottom: '0.25rem' }}
                                                />
                                                <input
                                                    value={step.config.lowLabel}
                                                    onChange={(e) => updateStepConfig(step.id, { lowLabel: e.target.value })}
                                                    placeholder="Low label"
                                                    style={{ ...triggerFieldStyle, padding: '0.35rem', marginBottom: '0.25rem' }}
                                                />
                                                <input
                                                    value={step.config.highLabel}
                                                    onChange={(e) => updateStepConfig(step.id, { highLabel: e.target.value })}
                                                    placeholder="High label"
                                                    style={{ ...triggerFieldStyle, padding: '0.35rem' }}
                                                />
                                            </div>
                                        ) : null}
                                        {step.type === STEP_TYPES.MULTIPLE_CHOICE ? (
                                            <div style={{ marginTop: '0.35rem' }}>
                                                <input
                                                    value={step.config.question}
                                                    maxLength={100}
                                                    onChange={(e) => updateStepConfig(step.id, { question: e.target.value.slice(0, 100) })}
                                                    placeholder="Question"
                                                    style={{ ...triggerFieldStyle, padding: '0.35rem', marginBottom: '0.25rem' }}
                                                />
                                                {(step.config.options || []).map((opt, oi) => (
                                                    <input
                                                        key={oi}
                                                        value={opt}
                                                        maxLength={60}
                                                        onChange={(e) => {
                                                            const opts = [...(step.config.options || [])];
                                                            opts[oi] = e.target.value.slice(0, 60);
                                                            updateStepConfig(step.id, { options: opts });
                                                        }}
                                                        placeholder={`Option ${oi + 1}`}
                                                        style={{ ...triggerFieldStyle, padding: '0.35rem', marginBottom: '0.2rem' }}
                                                    />
                                                ))}
                                                <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', marginTop: '0.25rem', flexWrap: 'wrap' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const opts = [...(step.config.options || [])];
                                                            if (opts.length < 5) opts.push('');
                                                            updateStepConfig(step.id, { options: opts });
                                                        }}
                                                        style={{ fontSize: '0.72rem', border: 'none', background: 'none' }}
                                                    >
                                                        + option
                                                    </button>
                                                    {(step.config.options || []).length > 2 ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const opts = [...(step.config.options || [])].slice(0, -1);
                                                                updateStepConfig(step.id, { options: opts });
                                                            }}
                                                            style={{ fontSize: '0.72rem', border: 'none', background: 'none' }}
                                                        >
                                                            − option
                                                        </button>
                                                    ) : null}
                                                    <label style={{ fontSize: '0.72rem' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!step.config.multiSelect}
                                                            onChange={(e) => updateStepConfig(step.id, { multiSelect: e.target.checked })}
                                                        />{' '}
                                                        Multi-select
                                                    </label>
                                                </div>
                                            </div>
                                        ) : null}
                                        {step.type === STEP_TYPES.TEXT_INPUT ? (
                                            <div style={{ marginTop: '0.35rem' }}>
                                                <input
                                                    value={step.config.prompt}
                                                    maxLength={120}
                                                    onChange={(e) => updateStepConfig(step.id, { prompt: e.target.value.slice(0, 120) })}
                                                    placeholder="Prompt"
                                                    style={{ ...triggerFieldStyle, padding: '0.35rem', marginBottom: '0.25rem' }}
                                                />
                                                <input
                                                    value={step.config.hint || ''}
                                                    maxLength={80}
                                                    onChange={(e) => updateStepConfig(step.id, { hint: e.target.value.slice(0, 80) })}
                                                    placeholder="Hint (optional)"
                                                    style={{ ...triggerFieldStyle, padding: '0.35rem', marginBottom: '0.25rem' }}
                                                />
                                                <label style={{ fontSize: '0.72rem', display: 'block', marginBottom: '0.25rem' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={!step.config.required}
                                                        onChange={(e) => updateStepConfig(step.id, { required: !e.target.checked })}
                                                    />{' '}
                                                    Optional
                                                </label>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={400}
                                                    value={step.config.charLimit}
                                                    onChange={(e) => updateStepConfig(step.id, { charLimit: Math.min(400, Math.max(1, Number(e.target.value) || 200)) })}
                                                    style={{ ...triggerFieldStyle, padding: '0.35rem' }}
                                                />
                                            </div>
                                        ) : null}
                                        {step.type === STEP_TYPES.BREATH_PACER ? (
                                            <div style={{ marginTop: '0.35rem' }}>
                                                <select
                                                    value={step.config.pattern}
                                                    onChange={(e) => updateStepConfig(step.id, { pattern: e.target.value })}
                                                    style={{ ...triggerFieldStyle, padding: '0.35rem', marginBottom: '0.25rem' }}
                                                >
                                                    <option value="box">Box Breathing (4-4-4-4)</option>
                                                    <option value="extendedExhale">Extended Exhale (4-8)</option>
                                                </select>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={5}
                                                    value={step.config.cycles}
                                                    onChange={(e) => updateStepConfig(step.id, { cycles: Math.min(5, Math.max(1, Number(e.target.value) || 1)) })}
                                                    style={{ ...triggerFieldStyle, padding: '0.35rem', marginBottom: '0.25rem' }}
                                                />
                                                <input
                                                    value={step.config.label || ''}
                                                    maxLength={60}
                                                    onChange={(e) => updateStepConfig(step.id, { label: e.target.value.slice(0, 60) })}
                                                    placeholder="Label during drill (optional)"
                                                    style={{ ...triggerFieldStyle, padding: '0.35rem' }}
                                                />
                                            </div>
                                        ) : null}
                                        {step.type === STEP_TYPES.REFLECTION_CLOSE ? (
                                            <textarea
                                                value={step.config.message}
                                                maxLength={200}
                                                rows={2}
                                                onChange={(e) => updateStepConfig(step.id, { message: e.target.value.slice(0, 200) })}
                                                placeholder="Closing message"
                                                style={{ ...triggerFieldStyle, padding: '0.35rem', marginTop: '0.35rem', resize: 'vertical' }}
                                            />
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div style={triggerFlowFooterStyle}>
                        <button type="button" className="popupButton" onClick={() => setBuilderPhase(3)}>
                            Continue
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <div style={triggerFlowScrollStyle}>
                        <p className="questionInfo" style={{ textAlign: 'left', opacity: 0.7 }}>Review &amp; Save</p>
                        <h2 style={{ fontSize: '1.02rem', fontWeight: 600 }}>{draft.name || 'Untitled'}</h2>
                        <p style={{ fontSize: '0.82rem', opacity: 0.75 }}>{draft.description || 'No description'}</p>
                        <p style={{ fontSize: '0.82rem', marginTop: '0.35rem' }}>Pillar: {draft.pillar}</p>
                        <p style={{ fontSize: '0.82rem' }}>~{estMin} min · {(draft.steps || []).length} steps</p>
                        <ol style={{ paddingLeft: '1.1rem', marginTop: '0.5rem', fontSize: '0.82rem' }}>
                            {(draft.steps || []).map((s) => (
                                <li key={s.id}>{s.type}</li>
                            ))}
                        </ol>
                        {!validation.ok ? <p style={{ color: '#8b2942', fontSize: '0.78rem', marginTop: '0.5rem' }}>Complete required fields to save.</p> : null}
                    </div>
                    <div style={{ ...triggerFlowFooterStyle, gap: '0.45rem' }}>
                        <button
                            type="button"
                            className="popupButton"
                            style={{ background: '#6a6a6a' }}
                            onClick={() => {
                                const steps = normalizeStepsOrder(draft.steps || []);
                                const w = {
                                    id: draft.editingId || newWorkoutId(),
                                    name: draft.name.trim(),
                                    description: (draft.description || '').trim(),
                                    pillar: draft.pillar,
                                    estimatedMinutes: estimateWorkoutMinutesFromSteps(steps),
                                    isCustom: true,
                                    createdBy: username,
                                    steps,
                                    createdAt: draft.editingId ? savedWorkouts.find((x) => x.id === draft.editingId)?.createdAt || Date.now() : Date.now(),
                                    updatedAt: Date.now(),
                                };
                                startPlayer(w, true);
                            }}
                        >
                            Preview
                        </button>
                        <button type="button" className="popupButton" disabled={!validation.ok} style={{ opacity: validation.ok ? 1 : 0.45 }} onClick={saveWorkoutToStorage}>
                            Save Workout
                        </button>
                    </div>
                </>
            )}
        </div>
    );

    const renderPlayerStep = () => {
        if (!currentPlayerStep) return null;
        const sid = currentPlayerStep.id;
        if (currentPlayerStep.type === STEP_TYPES.SLIDER) {
            return (
                <>
                    <div style={triggerFlowScrollStyle}>
                        <h2 style={{ textAlign: 'center', fontSize: '1.05rem', fontWeight: 600, marginTop: '0.35rem' }}>{currentPlayerStep.config.question}</h2>
                        <div style={{ padding: '0 0.5rem', marginTop: '1rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', opacity: 0.65, marginBottom: '0.35rem' }}>
                                <span>{currentPlayerStep.config.lowLabel}</span>
                                <span>{currentPlayerStep.config.highLabel}</span>
                            </div>
                            <input type="range" min={1} max={10} value={pvSlider} onChange={(e) => setPvSlider(Number(e.target.value))} style={{ width: '100%' }} />
                            <div style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '1.5rem', fontWeight: 700 }}>{pvSlider}</div>
                        </div>
                    </div>
                    <div style={triggerFlowFooterStyle}>
                        <button type="button" className="popupButton" onClick={() => advancePlayer([{ stepId: sid, type: STEP_TYPES.SLIDER, value: pvSlider }])}>
                            Continue
                        </button>
                    </div>
                </>
            );
        }
        if (currentPlayerStep.type === STEP_TYPES.MULTIPLE_CHOICE) {
            const opts = (currentPlayerStep.config.options || []).map((o) => String(o).trim()).filter(Boolean);
            const multi = !!currentPlayerStep.config.multiSelect;
            const canContinue = multi ? pvMc.length > 0 : pvMc.length === 1;
            return (
                <>
                    <div style={triggerFlowScrollStyle}>
                        <h2 style={{ textAlign: 'left', fontSize: '1.05rem', fontWeight: 600, marginTop: '0.35rem' }}>{currentPlayerStep.config.question}</h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.65rem' }}>
                            {opts.map((o) => (
                                <button
                                    key={o}
                                    type="button"
                                    onClick={() => {
                                        if (multi) {
                                            setPvMc((prev) => (prev.includes(o) ? prev.filter((x) => x !== o) : [...prev, o]));
                                        } else {
                                            setPvMc([o]);
                                        }
                                    }}
                                    style={{
                                        textAlign: 'left',
                                        padding: '0.65rem',
                                        borderRadius: '0.5rem',
                                        border: pvMc.includes(o) ? '2px solid #404040' : '1px solid #d8d6d3',
                                        background: '#EFEEED',
                                    }}
                                >
                                    {o}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div style={triggerFlowFooterStyle}>
                        <button type="button" className="popupButton" disabled={!canContinue} style={{ opacity: canContinue ? 1 : 0.45 }} onClick={() => advancePlayer([{ stepId: sid, type: STEP_TYPES.MULTIPLE_CHOICE, value: multi ? pvMc : pvMc[0] }])}>
                            Continue
                        </button>
                    </div>
                </>
            );
        }
        if (currentPlayerStep.type === STEP_TYPES.TEXT_INPUT) {
            const lim = Math.min(400, Math.max(1, Number(currentPlayerStep.config.charLimit) || 200));
            const req = !!currentPlayerStep.config.required;
            const ok = req ? pvText.trim().length > 0 : true;
            return (
                <>
                    <div style={triggerFlowScrollStyle}>
                        <h2 style={{ textAlign: 'left', fontSize: '1.05rem', fontWeight: 600, marginTop: '0.35rem' }}>{currentPlayerStep.config.prompt}</h2>
                        {currentPlayerStep.config.hint ? <p className="questionInfo" style={{ textAlign: 'left', marginTop: '0.45rem' }}>{currentPlayerStep.config.hint}</p> : null}
                        <textarea
                            value={pvText}
                            maxLength={lim}
                            onChange={(e) => setPvText(e.target.value.slice(0, lim))}
                            rows={4}
                            style={{ ...triggerFieldStyle, padding: '0.55rem', borderRadius: '0.4rem', marginTop: '0.55rem', resize: 'vertical' }}
                        />
                        <p className="questionInfo" style={{ textAlign: 'left', fontSize: '0.72rem' }}>{pvText.length}/{lim}</p>
                    </div>
                    <div style={triggerFlowFooterStyle}>
                        {req ? (
                            <button type="button" className="popupButton" disabled={!ok} style={{ opacity: ok ? 1 : 0.45 }} onClick={() => advancePlayer([{ stepId: sid, type: STEP_TYPES.TEXT_INPUT, value: pvText.trim() }])}>
                                Continue
                            </button>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', width: '100%', alignItems: 'center' }}>
                                <button type="button" className="popupButton" onClick={() => advancePlayer([{ stepId: sid, type: STEP_TYPES.TEXT_INPUT, value: pvText.trim() }])}>
                                    Continue
                                </button>
                                <button type="button" onClick={() => advancePlayer([{ stepId: sid, type: STEP_TYPES.TEXT_INPUT, value: '' }])} style={{ border: 'none', background: 'none', fontSize: '0.82rem' }}>
                                    Skip
                                </button>
                            </div>
                        )}
                    </div>
                </>
            );
        }
        if (currentPlayerStep.type === STEP_TYPES.BREATH_PACER) {
            const cycles = Math.min(5, Math.max(1, Number(currentPlayerStep.config.cycles) || 1));
            const lbl = currentPlayerStep.config.label;
            const fullBleed = true;
            return (
                <div
                    style={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: fullBleed ? '#0d0d0d' : undefined,
                        color: fullBleed ? '#e8e8e8' : undefined,
                        marginLeft: fullBleed ? '-0.75rem' : 0,
                        marginRight: fullBleed ? '-0.75rem' : 0,
                        width: fullBleed ? 'calc(100% + 1.5rem)' : '100%',
                    }}
                >
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
                        {lbl ? <p style={{ fontSize: '0.85rem', opacity: 0.85, marginBottom: '0.75rem', textAlign: 'center' }}>{lbl}</p> : null}
                        <div
                            style={{
                                width: 132,
                                height: 132,
                                borderRadius: '50%',
                                border: '2px solid #5a5a5a',
                                transform: `scale(${breathUi.scale})`,
                                transition: 'transform 0.35s ease-out',
                                background: 'radial-gradient(circle at 30% 30%, #2a2a2a, #0d0d0d)',
                            }}
                        />
                        <p style={{ marginTop: '1rem', fontSize: '0.68rem', letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.85 }}>{breathUi.label}</p>
                        <p style={{ marginTop: '0.35rem', fontSize: '0.75rem', opacity: 0.55 }}>
                            Cycle {breathUi.cycle} of {cycles}
                        </p>
                    </div>
                    <div style={{ flexShrink: 0, padding: '0.75rem', borderTop: '1px solid #2a2a2a' }}>
                        {breathDone ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', width: '100%', alignItems: 'center' }}>
                                <button
                                    type="button"
                                    className="popupButton"
                                    style={{ background: '#404040', color: '#fff' }}
                                    onClick={() => advancePlayer([{ stepId: sid, type: STEP_TYPES.BREATH_PACER, value: { completed: true, cycles } }])}
                                >
                                    Continue
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setBreathDone(false);
                                        setBreathRepeatNonce((n) => n + 1);
                                    }}
                                    style={{ border: 'none', background: 'none', color: '#cfcfcf', fontSize: '0.82rem' }}
                                >
                                    Repeat this breath
                                </button>
                            </div>
                        ) : (
                            <p style={{ textAlign: 'center', fontSize: '0.78rem', opacity: 0.6 }}>Breathe with the circle…</p>
                        )}
                    </div>
                </div>
            );
        }
        if (currentPlayerStep.type === STEP_TYPES.REFLECTION_CLOSE) {
            return (
                <>
                    <div style={triggerFlowScrollStyle}>
                        <h2 style={{ textAlign: 'center', fontSize: '1.08rem', fontWeight: 600, marginTop: '0.5rem' }}>Workout complete</h2>
                        <p style={{ textAlign: 'center', fontSize: '1rem', lineHeight: 1.5, marginTop: '1rem', fontFamily: 'serif', fontStyle: 'italic' }}>{currentPlayerStep.config.message}</p>
                    </div>
                    <div style={triggerFlowFooterStyle}>
                        <button type="button" className="popupButton" onClick={() => advancePlayer([{ stepId: sid, type: STEP_TYPES.REFLECTION_CLOSE, value: true }])}>
                            Log Rep
                        </button>
                    </div>
                </>
            );
        }
        return null;
    };

    const renderPlayer = () => (
        <div style={triggerFlowShellStyle}>
            {playerStepIndex > 0 && currentPlayerStep?.type !== STEP_TYPES.BREATH_PACER && currentPlayerStep?.type !== STEP_TYPES.REFLECTION_CLOSE ? (
                <div className="popupButtonGroup" style={{ position: 'static', width: '100%', flexShrink: 0, marginTop: 0, marginBottom: '0.25rem' }}>
                    <button type="button" className="popupNavigationBttn" onClick={() => setPlayerStepIndex((i) => Math.max(0, i - 1))} style={{ border: 'none', background: 'none' }}>
                        <img className="previousBttn" src={nextBttn} alt="back" />
                    </button>
                </div>
            ) : null}
            {renderPlayerStep()}
        </div>
    );

    const cardBg = '#3D414D';

    return (
        <>
            <div className="category-container" style={{ marginTop: 0, paddingTop: 0 }}>
                {savedWorkouts.length === 0 ? (
                    <p className="questionInfo" style={{ width: '100%', maxWidth: '100%', margin: '0 auto 1rem', opacity: 0.75 }}>
                        No saved workouts yet — tap Create workout to build your first guided session.
                    </p>
                ) : null}
                <div className="grid">
                    <div
                        onClick={beginCreateWorkout}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                beginCreateWorkout();
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        <WellnessGymCard title="Create workout" subtitle="Build your own guided session" image="" bgColor={cardBg} />
                    </div>
                    {savedWorkouts.map((w) => (
                        <div
                            key={w.id}
                            onClick={() => startPlayer(w, false)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    startPlayer(w, false);
                                }
                            }}
                            role="button"
                            tabIndex={0}
                        >
                            <WellnessGymCard
                                title={w.name}
                                subtitle={`${w.pillar} · ~${w.estimatedMinutes ?? '?'} min`}
                                image=""
                                bgColor={cardBg}
                            />
                        </div>
                    ))}
                </div>
            </div>

            <div className="popupContainer" id="custom-workout-modal" style={{ display: 'none', zIndex: 110 }}>
                <div className="formContainer" style={{ height: '62%', minHeight: 620, ...centeredFormContainerStyle }}>
                    <div className="button-group" style={{ float: 'right' }}>
                        <button type="button" onClick={requestCloseModal} style={{ border: 'none' }}>
                            <img className="closeBttn" src={closeBttn} alt="Close" />
                        </button>
                    </div>
                    <h2 style={{ textAlign: 'center', marginTop: '3rem', marginBottom: '0.5rem' }}>{modalView === 'player' ? playerWorkout?.name || 'Workout' : 'Custom Workout Builder'}</h2>
                    {modalView === 'player' ? (
                        <p className="questionInfo" style={{ width: '100%', maxWidth: '100%', margin: '0 auto 0.25rem', fontSize: '0.62rem', letterSpacing: '0.1em', opacity: 0.55 }}>
                            Custom
                        </p>
                    ) : null}
                    {modalView === 'player' && !playerPreview && playerWorkout && playerStepIndex === 0 ? (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '1rem',
                                marginBottom: '0.5rem',
                                flexWrap: 'wrap',
                            }}
                        >
                            <button
                                type="button"
                                style={{
                                    border: 'none',
                                    background: 'none',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    color: '#404040',
                                    fontSize: '0.82rem',
                                    padding: '0.2rem',
                                }}
                                onClick={() => editWorkout(playerWorkout)}
                            >
                                Edit workout
                            </button>
                            <button
                                type="button"
                                style={{
                                    border: 'none',
                                    background: 'none',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    color: '#8b2942',
                                    fontSize: '0.82rem',
                                    padding: '0.2rem',
                                }}
                                onClick={() => setDeleteConfirmId(playerWorkout.id)}
                            >
                                Delete workout
                            </button>
                        </div>
                    ) : null}
                    <div className="learningTextContent" style={{ overflow: 'hidden', height: '80%', minHeight: 0, textAlign: 'center', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
                        {modalView === 'builder' ? renderBuilder() : modalView === 'player' ? renderPlayer() : null}
                    </div>
                    <div className="popupButtonGroup" />
                </div>
            </div>

            {deleteConfirmId ? (
                <div className="popupContainer" style={{ display: 'flex', visibility: 'visible', zIndex: 120 }}>
                    <div className="formContainer" style={{ height: 'auto', minHeight: 200, maxWidth: 360, ...centeredFormContainerStyle }}>
                        <p style={{ textAlign: 'center', marginTop: '2rem', padding: '0 1rem' }}>Delete this workout? This cannot be undone.</p>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', margin: '1.25rem 0' }}>
                            <button type="button" className="popupButton" style={{ width: 'auto', padding: '0.75rem 1.25rem', background: '#8b2942' }} onClick={() => deleteWorkout(deleteConfirmId)}>
                                Delete
                            </button>
                            <button type="button" className="popupButton" style={{ width: 'auto', padding: '0.75rem 1.25rem', background: '#6a6a6a' }} onClick={() => setDeleteConfirmId(null)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {exitSessionConfirm ? (
                <div className="popupContainer" style={{ display: 'flex', visibility: 'visible', zIndex: 120 }}>
                    <div className="formContainer" style={{ height: 'auto', minHeight: 200, maxWidth: 380, ...centeredFormContainerStyle }}>
                        <p style={{ textAlign: 'center', marginTop: '2rem', padding: '0 1rem' }}>Leave this workout? Your progress may be logged as incomplete.</p>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', margin: '1.25rem 0' }}>
                            <button type="button" className="popupButton" style={{ width: 'auto', padding: '0.75rem 1.25rem' }} onClick={confirmExitPlayer}>
                                Leave
                            </button>
                            <button type="button" className="popupButton" style={{ width: 'auto', padding: '0.75rem 1.25rem', background: '#6a6a6a' }} onClick={() => setExitSessionConfirm(false)}>
                                Stay
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
}
